import React, { useState } from 'react';
import { Text, FontIcon, mergeStyles, IStyle, getTheme, ITheme } from '@fluentui/react';
import { BwjInput } from '../../entities/bwj-input';
import { BwjSpinnerLabel } from '../bwj-spinner-label';
import { BwjWarningLabel } from '../bwj-warning-label';
import { IBwjInputProps } from '../bwj-input-base';
import { IBwjUploadFieldPrompt } from './entities';
import { BwjPillbox } from '../bwj-pill-box';
import { getBwjUploadStyles } from './styles';
import { BwjValidityState } from '../../entities/bwj-validity-state';

interface IBwjUploadFieldUiProps extends IBwjInputProps<File[]> {
  onDrop: React.DragEventHandler<HTMLDivElement>;
  onDragOver: React.DragEventHandler<HTMLDivElement>;
  onSelectFileChange: React.ChangeEventHandler<HTMLInputElement>;
  onRemoveFile: (file: File) => void;
  isDragging: boolean;
  disabled: boolean;
  uploadPrompt?: IBwjUploadFieldPrompt;
  rootStyle?: IStyle;
  wrapperStyle?: IStyle;
  labelStyle?: IStyle;
  fieldStyle?: IStyle;
  uploadIconStyle?: IStyle;
  dropZonePromptStyle?: IStyle;
}

const onRenderStateMessage = (input: BwjInput<any>, theme: ITheme) => {
  switch(input.validity) {
    case BwjValidityState.Valid:
      return (<Text variant="small">{input.message}</Text>);
    case BwjValidityState.Indeterminate:
      return (<BwjSpinnerLabel text={input.message} />);
    case BwjValidityState.Warning:
      return (<BwjWarningLabel text={input.message} />);
    default:
      return (<Text variant="small" style={{ color: theme.semanticColors.errorText }}>{input.message}</Text>);
  }
};

const toContentString = (file: File) => file.name;

export const BwjUploadFieldUi: React.FunctionComponent<IBwjUploadFieldUiProps> = (props) => {

  const {id, rootStyle, wrapperStyle, labelStyle, fieldStyle, uploadIconStyle, dropZonePromptStyle, input, required, isDragging, disabled} = props;
  const styles = getBwjUploadStyles(disabled);

  const [theme] = useState(getTheme());

  const rootClassName = (() => {
    const mergedStyle: IStyle[] = [styles.uploadField];
    !!rootStyle && mergedStyle.push(rootStyle);

    return mergeStyles(mergedStyle);
  })();

  const wrapperClassName = (() => {
    const mergedStyles: IStyle[] = [];
    !!wrapperStyle && mergedStyles.push(wrapperStyle);

    return mergeStyles(mergedStyles);
  })();

  const labelClassName = (() => {
    const mergedStyles: IStyle[] = [styles.label];
    !!labelStyle && mergedStyles.push(labelStyle);
    required && mergedStyles.push(styles.label_required);

    return mergeStyles(mergedStyles);
  })();

  const dropZoneClassName = (() => {
    const mergedStyles: IStyle[] = [styles.dropZone];
    !!fieldStyle && mergedStyles.push(fieldStyle);

    if(!disabled) {
      if(isDragging) {
        mergedStyles.push(styles.dropZone_hot);
      }
      else if(input.validity === BwjValidityState.Invalid) {
        mergedStyles.push(styles.dropZone_invalid);
      }
    }
    if(isDragging) {
      mergedStyles.push({
        cursor: !disabled ? 'grabbing' : 'no-drop',
      });
    }

    return mergeStyles(mergedStyles);
  })();

  const uploadIconClassName = (() => {
    const mergedStyles: IStyle[] = [styles.uploadIcon];
    !!uploadIconStyle && mergedStyles.push(uploadIconStyle);

    return mergeStyles(mergedStyles);
  })();

  const dropZonePromptClassName = (() => {
    const mergedStyles: IStyle[] = [styles.dropZonePrompt];
    !!dropZonePromptStyle && mergedStyles.push(dropZonePromptStyle);

    return mergeStyles(mergedStyles);
  })();

  const fileSelectPrompt = mergeStyles([styles.fileSelectPrompt]);
  const fileSelectPromptLabel = mergeStyles([styles.fileSelectPromptLabel]);

  return (
    <div className={rootClassName}>
      <div className={wrapperClassName}>
        {!!props.label && <label
          id={`${id}-label`}
          className={labelClassName}
          htmlFor={id}
        >
          {props.label}
        </label>}
        <div
          id={id}
          className={dropZoneClassName}
          onDragOver={props.onDragOver}
          onDrop={props.onDrop}
        >
          <FontIcon iconName='CloudUpload' className={uploadIconClassName} />
          <p className={dropZonePromptClassName}>
            <span>
              {!!props.uploadPrompt ? props.uploadPrompt.dropZonePrompt : 'Drag and drop file here, or '}
            </span>
            <button
              className={fileSelectPrompt}
              onFocus={() => !!props.onFocus && props.onFocus()}
              onBlur={() => !!props.onBlur && props.onBlur()}
            >
              <label
                htmlFor={`${id}-upload-input`}
                className={fileSelectPromptLabel}
              >
                {!!props.uploadPrompt ? props.uploadPrompt.selectFilePrompt : 'click here to select a file'}
              </label>
              <input
                id={`${id}-upload-input`}
                disabled={disabled}
                type="file"
                onChange={props.onSelectFileChange}
                style={{display: 'none'}}
              />
            </button>
          </p>
        </div>
      </div>
      {!!props.input.message && onRenderStateMessage(props.input, theme)}
      <BwjPillbox
        getKey={(file: File) => `${file.name}.${file.size}.${file.lastModified}`}
        content={input.value}
        toContentString={toContentString}
        onActionButtonClick={props.onRemoveFile}
        disabled={disabled}
      />
    </div>
  );
};
